<script>
import { onMount, onDestroy } from 'svelte';
import { modalState, hideModal } from '@app/store/modalStore.svelte.js';
import { projectState } from '@app/store/projectStore.svelte.js';
import { eventGroupState } from '@app/store/eventGroupStore.svelte.js';
import { editorState } from '@app/store/editorStore.svelte.js';
import { speakerState } from '@app/store/speakerStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import { fade } from 'svelte/transition';
import { loadFfmpeg } from '@app/lib/loadFfmpeg.js';
import tcLib from "@cclib/lib/timecode.js";

const whisperLanguages = [
    { code: "en", name: "English" },
    { code: "zh", name: "Chinese" },
    { code: "de", name: "German" },
    { code: "es", name: "Spanish" },
    { code: "ru", name: "Russian" },
    { code: "ko", name: "Korean" },
    { code: "fr", name: "French" },
    { code: "ja", name: "Japanese" },
    { code: "pt", name: "Portuguese" },
    { code: "tr", name: "Turkish" },
    { code: "pl", name: "Polish" },
    { code: "ca", name: "Catalan" },
    { code: "nl", name: "Dutch" },
    { code: "ar", name: "Arabic" },
    { code: "sv", name: "Swedish" },
    { code: "it", name: "Italian" },
    { code: "id", name: "Indonesian" },
    { code: "hi", name: "Hindi" },
    { code: "fi", name: "Finnish" },
    { code: "vi", name: "Vietnamese" },
    { code: "he", name: "Hebrew" },
    { code: "uk", name: "Ukrainian" },
    { code: "el", name: "Greek" },
    { code: "ms", name: "Malay" },
    { code: "cs", name: "Czech" },
    { code: "ro", name: "Romanian" },
    { code: "da", name: "Danish" },
    { code: "hu", name: "Hungarian" },
    { code: "ta", name: "Tamil" },
    { code: "no", name: "Norwegian" },
    { code: "th", name: "Thai" },
    { code: "ur", name: "Urdu" },
    { code: "hr", name: "Croatian" },
    { code: "bg", name: "Bulgarian" },
    { code: "lt", name: "Lithuanian" },
    { code: "la", name: "Latin" },
    { code: "mi", name: "Maori" },
    { code: "ml", name: "Malayalam" },
    { code: "cy", name: "Welsh" },
    { code: "sk", name: "Slovak" },
    { code: "te", name: "Telugu" },
    { code: "fa", name: "Persian" },
    { code: "lv", name: "Latvian" },
    { code: "bn", name: "Bengali" },
    { code: "sr", name: "Serbian" },
    { code: "az", name: "Azerbaijani" },
    { code: "sl", name: "Slovenian" },
    { code: "kn", name: "Kannada" },
    { code: "et", name: "Estonian" },
    { code: "mk", name: "Macedonian" },
    { code: "br", name: "Breton" },
    { code: "eu", name: "Basque" },
    { code: "is", name: "Icelandic" },
    { code: "hy", name: "Armenian" },
    { code: "ne", name: "Nepali" },
    { code: "mn", name: "Mongolian" },
    { code: "bs", name: "Bosnian" },
    { code: "kk", name: "Kazakh" },
    { code: "sq", name: "Albanian" },
    { code: "sw", name: "Swahili" },
    { code: "gl", name: "Galician" },
    { code: "mr", name: "Marathi" },
    { code: "pa", name: "Punjabi" },
    { code: "si", name: "Sinhala" },
    { code: "km", name: "Khmer" },
    { code: "sn", name: "Shona" },
    { code: "yo", name: "Yoruba" },
    { code: "so", name: "Somali" },
    { code: "af", name: "Afrikaans" },
    { code: "oc", name: "Occitan" },
    { code: "ka", name: "Georgian" },
    { code: "be", name: "Belarusian" },
    { code: "tg", name: "Tajik" },
    { code: "sd", name: "Sindhi" },
    { code: "gu", name: "Gujarati" },
    { code: "am", name: "Amharic" },
    { code: "yi", name: "Yiddish" },
    { code: "lo", name: "Lao" },
    { code: "uz", name: "Uzbek" },
    { code: "fo", name: "Faroese" },
    { code: "ht", name: "Haitian Creole" },
    { code: "ps", name: "Pashto" },
    { code: "tk", name: "Turkmen" },
    { code: "nn", name: "Nynorsk" },
    { code: "mt", name: "Maltese" },
    { code: "sa", name: "Sanskrit" },
    { code: "lb", name: "Luxembourgish" },
    { code: "my", name: "Myanmar" },
    { code: "bo", name: "Tibetan" },
    { code: "tl", name: "Tagalog" },
    { code: "mg", name: "Malagasy" },
    { code: "as", name: "Assamese" },
    { code: "tt", name: "Tatar" },
    { code: "haw", name: "Hawaiian" },
    { code: "ln", name: "Lingala" },
    { code: "ha", name: "Hausa" },
    { code: "ba", name: "Bashkir" },
    { code: "jw", name: "Javanese" },
    { code: "su", name: "Sundanese" },
];

const modelSizes = [
    { value: "tiny", label: "Tiny (~75 MB)", size: 75 },
    { value: "base", label: "Base (~142 MB)", size: 142 },
    { value: "small", label: "Small (~466 MB)", size: 466 },
    { value: "medium", label: "Medium (~1.5 GB)", size: 1500 },
    { value: "large-v3-turbo", label: "Large (~1.6 GB)", size: 1600 },
];

let language = $state("en");
let modelSize = $state("base");
let maxLines = $state(2);
let maxChars = $state(32);
let phase = $state("config"); // config | downloading | transcribing | importing
let progress = $state(0);
let statusMsg = $state("");
let modelDownloaded = $state(false);

let duration = 10;
let durationTc = "00:00:10:00";

let downloadListener = null;

onMount(async () => {
    await checkModelStatus();
});

onDestroy(() => {
    if (downloadListener) {
        window.ipcRenderer.removeListener("whisper-download-progress", downloadListener);
    }
});

async function checkModelStatus() {
    try {
        const result = await window.ipcRenderer.invoke("checkWhisperModel", { modelSize });
        modelDownloaded = result.exists;
    } catch {
        modelDownloaded = false;
    }
}

async function startTranscription() {
    try {
        /* Check if model is available, download if not */
        const modelCheck = await window.ipcRenderer.invoke("checkWhisperModel", { modelSize });

        if (!modelCheck.exists) {
            phase = "downloading";
            progress = 0;
            statusMsg = `Downloading ${modelSize} model...`;

            downloadListener = (_event, data) => {
                progress = data.percent;
                statusMsg = `Downloading ${modelSize} model... ${data.downloadedMB} / ${data.totalMB} MB`;
            };
            window.ipcRenderer.on("whisper-download-progress", downloadListener);

            try {
                await window.ipcRenderer.invoke("downloadWhisperModel", { modelSize });
            } finally {
                window.ipcRenderer.removeListener("whisper-download-progress", downloadListener);
                downloadListener = null;
            }

            modelDownloaded = true;
        }

        /* Get model path and start FFmpeg transcription */
        const modelInfo = await window.ipcRenderer.invoke("checkWhisperModel", { modelSize });
        const modelPath = modelInfo.path;
        const mediaPath = projectState.media.localPath;
        const tempJsonPath = window.path.join(window.os.tmpdir(), `whisper-${Date.now()}.json`);

        /* Strip Windows drive letter for FFmpeg filter paths (e.g. C:/Users/... -> /Users/...) */
        const ffmpegModelPath = modelPath.replace(/^[A-Za-z]:/, '').replace(/\\/g, '/');
        const ffmpegJsonPath = tempJsonPath.replace(/^[A-Za-z]:/, '').replace(/\\/g, '/');

        phase = "transcribing";
        progress = 0;
        statusMsg = "Starting transcription...";

        const ffmpeg = loadFfmpeg();

        ffmpeg(mediaPath)
            .noVideo()
            .outputOptions([
                `-af`, `whisper=model=${ffmpegModelPath}:language=${language}:destination=${ffmpegJsonPath}:format=json`,
                `-f`, `null`
            ])
            .output("-")
            .on('start', function (commandLine) {
                console.log('Spawned FFmpeg with command: ' + commandLine);
            })
            .on('codecData', function (data) {
                durationTc = data.duration;
                duration = tcLib.tcMsToSec(data.duration);
            })
            .on('progress', function (prog) {
                let tcSec = tcLib.tcMsToSec(prog.timemark);
                progress = ((tcSec / duration) * 100).toFixed(2);
                statusMsg = `Transcribing... ${prog.timemark} / ${durationTc} (${progress}%)`;
            })
            .on('end', async function () {
                try {
                    phase = "importing";
                    statusMsg = "Importing transcription...";
                    progress = 100;

                    const jsonContent = await window.fs.readFile(tempJsonPath, 'utf8');
                    const [
                        { default: decode },
                        { default: defaults },
                        { default: encodeHtml },
                        { default: fixOverlap },
                    ] = await Promise.all([
                        import("@cclib/functions/decode.js"),
                        import("@cclib/lib/defaults.js"),
                        import("@cclib/functions/eventGroups/encodeHtml.js"),
                        import("@cclib/functions/eventGroups/fixOverlap.js"),
                    ]);
                    const eventGroupDefaults = JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};
                    const options = new defaults.options({
                        profile: "whisper",
                        formatOptions: [
                            { name: "Import Type", type: "list", values: ["subtitle", "transcription"], selected: "subtitle" },
                            { name: "Max Characters", type: "number-input", values: "", selected: maxChars },
                            { name: "Max Lines", type: "number-input", values: "", selected: maxLines }
                        ],
                        frameRate: projectState.frameRate,
                        dropFrame: projectState.dropFrame
                    });

                    // console.log("jsonContent", jsonContent);

                    let transcriptEventGroup = { ...(await decode(jsonContent, options)) };
                    transcriptEventGroup = encodeHtml(transcriptEventGroup);
                    transcriptEventGroup = fixOverlap(transcriptEventGroup, editorState.minFrameGap, projectState.frameRate);

                    transcriptEventGroup.type = "subtitle";
                    transcriptEventGroup.name = `Offline Transcription ${eventGroupState.length + 1}`;
                    transcriptEventGroup.language = language;
                    transcriptEventGroup.maxCps = eventGroupDefaults.maxCps || 9999;
                    transcriptEventGroup.maxWpm = eventGroupDefaults.maxWpm || 9999;
                    transcriptEventGroup.maxChars = eventGroupDefaults.maxChars || 9999;
                    transcriptEventGroup.maxLines = eventGroupDefaults.maxLines || 9999;
                    transcriptEventGroup.minDuration = eventGroupDefaults.minDuration || 0.5;
                    transcriptEventGroup.maxDuration = eventGroupDefaults.maxDuration || 12;
                    transcriptEventGroup.overlap = eventGroupDefaults.overlap;
                    transcriptEventGroup.fontFamily = eventGroupDefaults.fontFamily;
                    transcriptEventGroup.fontSize = eventGroupDefaults.fontSize || 16;

                    eventGroupState.push({ ...transcriptEventGroup });
                    projectState.selected = eventGroupState.length - 1;

                    if (transcriptEventGroup.speakers) {
                        transcriptEventGroup.speakers.forEach(sp => {
                            speakerState.push(sp);
                        });
                    }

                    /* Clean up temp file */
                    try { await window.fs.unlink(tempJsonPath); } catch {}

                    toast.push("Offline transcription completed successfully.", { classes: ['toast-success'] });
                    hideModal();
                } catch (err) {
                    console.error("Import error:", err);
                    toast.push(`Failed to import transcription: ${err.message}`, { classes: ['toast-warning'] });
                    phase = "config";
                }
            })
            .on('error', function (err) {
                console.error("FFmpeg error:", err);
                toast.push(`Transcription failed: ${err.message}`, { classes: ['toast-warning'] });
                phase = "config";
            })
            .run();

    } catch (err) {
        console.error("Error:", err);
        toast.push(`Error: ${err.message}`, { classes: ['toast-warning'] });
        phase = "config";
    }
}
</script>

<div transition:fade={{ duration: 100 }} class="modal {modalState.selected === 'offlineTranscription' ? 'show d-block' : ''}" role="dialog" aria-labelledby="offlineTranscriptionTitle" tabindex="-1" id="OfflineTranscriptionModal">
    <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="offlineTranscriptionTitle">Offline Transcription</h4>
                {#if phase === "config"}
                    <button type="button" class="btn-close" aria-label="Close" onclick={hideModal}></button>
                {/if}
            </div>
            <div class="modal-body">
                {#if phase === "config"}
                    <div class="mb-3">
                        <label for="whisperLanguageSelect" class="form-label">Source Language</label>
                        <select id="whisperLanguageSelect" class="form-select" bind:value={language} onchange={checkModelStatus}>
                            {#each whisperLanguages as lang}
                                <option value={lang.code}>{lang.name}</option>
                            {/each}
                        </select>
                    </div>
                    <div class="mb-3">
                        <label for="whisperModelSelect" class="form-label">Model Size</label>
                        <select id="whisperModelSelect" class="form-select" bind:value={modelSize} onchange={checkModelStatus}>
                            {#each modelSizes as model}
                                <option value={model.value}>{model.label}</option>
                            {/each}
                        </select>
                        <div class="form-text">
                            {#if modelDownloaded}
                                <span class="text-success"><i class="bi bi-check-circle-fill"></i> Model downloaded</span>
                            {:else}
                                <span class="text-muted"><i class="bi bi-cloud-download"></i> Model will be downloaded on first use</span>
                            {/if}
                        </div>
                    </div>
                    <div class="row mb-3">
                        <div class="col">
                            <label for="maxCharsInput" class="form-label">Max Characters</label>
                            <input id="maxCharsInput" type="number" class="form-control" bind:value={maxChars} min="10" max="80">
                        </div>
                        <div class="col">
                            <label for="maxLinesInput" class="form-label">Max Lines</label>
                            <input id="maxLinesInput" type="number" class="form-control" bind:value={maxLines} min="1" max="4">
                        </div>
                    </div>
                    <div class="d-grid">
                        <button class="btn btn-primary" onclick={startTranscription} disabled={!projectState.media.localPath}>
                            <i class="bi bi-mic-fill"></i> Transcribe
                        </button>
                    </div>
                    {#if !projectState.media.localPath}
                        <p class="text-center text-muted small mt-2">Please load a local media file to use offline transcription.</p>
                    {/if}
                {:else}
                    <p>
                        {#if phase === "downloading"}
                            <i class="bi bi-cloud-download" aria-hidden="true"></i> Downloading whisper model...
                        {:else if phase === "transcribing"}
                            <i class="bi bi-mic-fill" aria-hidden="true"></i> Transcribing media...
                        {:else if phase === "importing"}
                            <i class="bi bi-file-earmark-text" aria-hidden="true"></i> Importing results...
                        {/if}
                    </p>
                    <div class="progress" role="progressbar" aria-label="Transcription progress" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                        <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" style="width: {progress}%;">{progress}%</div>
                    </div>
                    <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
                {/if}
            </div>
        </div>
    </div>
</div>
